// Tags inferred from task titles, for reporting ("where did the hours go?") without tagging by hand.
// Local and explainable — no AI in this app (that's Willow's job):
//   1. LEARNED from you: every word in the title of a task YOU tagged counts toward those tags. A word that
//      has been seen on ≥ 2 tagged tasks and carries a tag on ≥ 60 % of them suggests it.
//   2. STARTER rules for a cold start (buy/order/pick up → material, frame/stud/joist → framing, …).
// Inferred tags live beside your tags, never in them: keep one (it becomes a real tag, and teaches the
// model) or reject it (task.tagsRejected). Bucket tasks (setup / cleanup) are reported as buckets already.
// Pure.
import { canonTag, resolveTag } from './tags.js';
import { projectTagMap } from './projecttags.js';

const STOP = new Set(('the a an and or of to for in on at with from by into onto off up out over under then '
  + 'is are be do get go make new old all any some this that these those it its my our your his her their '
  + 'one two three four five six seven eight nine ten each more less per via day today tomorrow').split(' '));

/** Title words: lowercase, no #tags / numbers / measurements, light plural + -ing/-ed trimming. */
export function titleTokens(text) {
  const words = String(text || '').toLowerCase()
    .replace(/#[\w-]+/g, ' ')
    .replace(/[0-9]+[\w/'".×x-]*/g, ' ')
    .split(/[^a-z]+/).filter((w) => w.length >= 3 && !STOP.has(w));
  const stem = (w) => (w.length > 5 && w.endsWith('ing') ? w.slice(0, -3)
    : w.length > 4 && w.endsWith('ed') ? w.slice(0, -2)
      : w.length > 3 && w.endsWith('s') && !w.endsWith('ss') ? w.slice(0, -1) : w);
  return [...new Set(words.map(stem))];
}

// tag → words (already in titleTokens form). Kept small and generic: they only start things off.
export const STARTER_TAG_RULES = {
  material: ['buy', 'order', 'pickup', 'pick', 'lumber', 'screw', 'nail', 'fastener', 'supplie', 'hardware', 'store'],
  framing: ['frame', 'fram', 'stud', 'joist', 'rafter', 'header', 'plate', 'sheath', 'truss', 'blocking', 'wall'],
  cutting: ['cut', 'rip', 'crosscut', 'miter', 'mitre'],
  layout: ['measure', 'layout', 'mark', 'square', 'level', 'plumb', 'string'],
  finishing: ['paint', 'prime', 'stain', 'sand', 'caulk', 'finish', 'trim'],
  cleanup: ['clean', 'sweep', 'tidy', 'dump', 'haul'],
  admin: ['invoice', 'email', 'quote', 'estimate', 'bid', 'call', 'text', 'receipt', 'bookkeep', 'book', 'bill', 'pay'],
  travel: ['drive', 'travel', 'commute'],
  dev: ['code', 'deploy', 'bug', 'app', 'api', 'build', 'commit', 'refactor'],
  marketing: ['post', 'reel', 'video', 'content', 'caption', 'script', 'film'],
};

/** Learn word → tag from the tasks you tagged yourself. */
export function buildTagModel(tasks = []) {
  const tokN = new Map();       // word → tagged tasks it appears in
  const pair = new Map();       // word → Map(tag → count)
  for (const t of tasks) {
    const tags = Array.isArray(t?.tags) ? t.tags.filter(Boolean) : [];
    if (!tags.length || t.bucket) continue;
    for (const w of titleTokens(t.title)) {
      tokN.set(w, (tokN.get(w) || 0) + 1);
      if (!pair.has(w)) pair.set(w, new Map());
      const m = pair.get(w);
      for (const g of tags) m.set(g, (m.get(g) || 0) + 1);
    }
  }
  const starter = new Map();    // word → [tag]
  for (const [tag, words] of Object.entries(STARTER_TAG_RULES)) for (const w of words) starter.set(w, [...(starter.get(w) || []), tag]);
  return { tokN, pair, starter };
}

/** Suggested tags for a task, best first: [{ tag, score, why }] (why = the word that suggested it). */
export function inferTags(task, model, { minSupport = 2, minShare = 0.6, max = 3, starter = true } = {}) {
  if (!task || task.bucket || !model) return [];
  const have = new Set(task.tags || []);
  const no = new Set(task.tagsRejected || []);
  const best = new Map();
  const offer = (tag, score, why) => {
    if (have.has(tag) || no.has(tag)) return;
    const cur = best.get(tag);
    if (!cur || score > cur.score) best.set(tag, { tag, score, why });
  };
  for (const w of titleTokens(task.title)) {
    const n = model.tokN.get(w) || 0;
    if (n >= minSupport) for (const [tag, c] of model.pair.get(w) || []) { const share = c / n; if (share >= minShare) offer(tag, share, w); }
    if (starter) for (const tag of model.starter.get(w) || []) offer(tag, 0.5, w);   // learned beats starter
  }
  return [...best.values()].sort((a, b) => b.score - a.score || a.tag.localeCompare(b.tag)).slice(0, max);
}

/** What a report counts for a task: your tags, else the best inferred one (flagged). */
export function reportTags(task, model, opts) {
  const own = Array.isArray(task?.tags) ? task.tags.filter(Boolean) : [];
  if (own.length) return { tags: own, auto: false };
  const guess = inferTags(task, model, { ...opts, max: 1 });
  return guess.length ? { tags: [guess[0].tag], auto: true } : { tags: [], auto: false };
}

/**
 * Time per tag. rows = [{ task, ms }]. A task with several tags counts its time under each (so the
 * rows can add up to more than the total — `totalMs` is the real total). Untagged time is its own row.
 * opts.projects (+ opts.tagIndex, the suite list): a project's tags count every hour on its tasks too
 * (projecttags.js) — `projectMs` is the part of a row that came "via project tag". A task that carries the same tag
 * as its project counts once, as its own tag (no double count). Setup / cleanup buckets count only via the project.
 * → { totalMs, rows: [{ tag, ms, tasks, autoMs, projectMs }] } most time first, 'untagged' last.
 */
export function timeByTag(rows = [], model, opts = {}) {
  const by = new Map();
  const projTags = opts?.projects ? projectTagMap(opts.projects, opts.tagIndex) : null;
  let totalMs = 0;
  const bump = (tag, ms, k) => {
    const cur = by.get(tag) || { tag, ms: 0, tasks: 0, autoMs: 0, projectMs: 0 };
    cur.ms += ms; cur.tasks += 1; if (k) cur[k] += ms;
    by.set(tag, cur);
  };
  for (const r of rows) {
    const ms = r?.ms || 0;
    if (ms <= 0 || !r.task) continue;
    const pTags = projTags && r.task.projectId ? projTags.get(r.task.projectId) || [] : [];
    if (r.task.bucket && !pTags.length) continue;
    totalMs += ms;
    const { tags, auto } = r.task.bucket ? { tags: [], auto: false } : reportTags(r.task, model, opts);
    const own = new Set(tags.map((t) => canonTag(opts?.tagIndex ? resolveTag(t, opts.tagIndex) : t)));
    const via = pTags.filter((t) => !own.has(t));
    for (const tag of tags) bump(tag, ms, auto ? 'autoMs' : null);
    for (const tag of via) bump(tag, ms, 'projectMs');
    if (!tags.length && !via.length) bump('untagged', ms, null);
  }
  const list = [...by.values()].sort((a, b) => (a.tag === 'untagged') - (b.tag === 'untagged') || b.ms - a.ms);
  return { totalMs, rows: list };
}

/** [{ task, ms }] for every task with time logged on a date in [from, to] (YYYY-MM-DD, inclusive). */
export function taskTimeBetween(tasks = [], from, to) {
  const out = [];
  for (const t of tasks) {
    const ms = (t?.timeEntries || []).filter((e) => e && e.date >= from && e.date <= to).reduce((s, e) => s + (e.duration || 0), 0);
    if (ms > 0) out.push({ task: t, ms });
  }
  return out;
}
